import { useAuth0 } from "@auth0/auth0-react";
import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import OpenAPIClientAxios from "openapi-client-axios";
import React, { createContext, useRef } from "react";

import { EnvVar } from "@/env";
import { useLogout } from "@/hooks/useLogout";
import { isUnauthorizedError } from "@/lib/axios";

import { Client } from "../schema/openapi";
import OpenApiDocument from "../schema/openapi.json";

type ApiContext = {
  apiClient: Client;
};

export const ApiContext = createContext<ApiContext | null>(null);

type ApiContextProviderProps = {
  children: React.ReactNode;
};

const ApiContextProvider = ({ children }: ApiContextProviderProps) => {
  const api = new OpenAPIClientAxios({
    definition: OpenApiDocument as any,
    withServer: {
      url: EnvVar.maiteUrl,
    },
  });

  const apiClient = api.initSync<Client>();

  const { getAccessTokenSilently } = useAuth0();
  const requestInterceptorIdRef = useRef<number | undefined>(undefined);
  const setRequestInterceptorId = (id: number | undefined) => {
    requestInterceptorIdRef.current = id;
  };

  const responseInterceptorIdRef = useRef<number | undefined>(undefined);
  const setResponseInterceptorId = (id: number | undefined) => {
    responseInterceptorIdRef.current = id;
  };

  const setupInterceptors = (apiClient: Client) => {
    if (requestInterceptorIdRef.current) {
      apiClient.interceptors.request.eject(requestInterceptorIdRef.current);
      setRequestInterceptorId(undefined);
    }

    if (responseInterceptorIdRef.current) {
      apiClient.interceptors.response.eject(responseInterceptorIdRef.current);
      setResponseInterceptorId(undefined);
    }

    const requestInterceptor = apiClient.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const tokenVerbose = await getAccessTokenSilently({
          cacheMode: "on",
          authorizationParams: {
            audience: EnvVar.auth0Audience,
            scope: "profile email read:current_user",
          },
          detailedResponse: true,
        });

        config.headers!.Authorization = `Bearer ${tokenVerbose.id_token}`;
        return config;
      },
      (error: unknown) => {
        console.error("Error in request interceptor:", error);
        return Promise.reject(error);
      }
    );

    const responseInterceptor = apiClient.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: unknown) => {
        if (!isUnauthorizedError(error)) {
          return Promise.reject(error);
        }

        try {
          const refreshTokenResponse = await getAccessTokenSilently({
            cacheMode: "off",
            authorizationParams: {
              audience: EnvVar.auth0Audience,
              scope: "profile email read:current_user",
            },
            detailedResponse: true,
          });

          const replayRequest: AxiosRequestConfig = {
            ...error.config,
            headers: {
              ...error.config?.headers,
              Authorization: `Bearer ${refreshTokenResponse.id_token}`,
            },
          };

          return axios(replayRequest);
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          return Promise.reject(refreshError);
        }
      }
    );

    setRequestInterceptorId(requestInterceptor);
    setResponseInterceptorId(responseInterceptor);
  };

  setupInterceptors(apiClient);

  return (
    <ApiContext.Provider
      value={{
        apiClient,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};

export default ApiContextProvider;
